import React from 'react';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { User, Mail, Phone, CreditCard, Calendar, Shield } from 'lucide-react';

const ProfilePage = () => {
  const { user } = useAuth();

  const profileFields = [
    { icon: User, label: 'Full Name', value: user.name, testId: 'profile-name' },
    { icon: CreditCard, label: 'Client ID', value: user.client_id, testId: 'profile-client-id' },
    { icon: Phone, label: 'Mobile Number', value: user.mobile, testId: 'profile-mobile' },
    { icon: Mail, label: 'Email', value: user.email, testId: 'profile-email' },
    { icon: Shield, label: 'PAN Number', value: user.pan, testId: 'profile-pan' },
    { icon: Calendar, label: 'Date of Birth', value: user.dob, testId: 'profile-dob' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" data-testid="profile-page-title">My Profile</h1>
          <p className="text-gray-600">View and manage your account information</p>
        </div>

        {/* Profile Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white mb-8" data-testid="profile-header">
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-4xl font-bold">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-2">{user.name}</h2>
              <p className="text-blue-100">Client ID: {user.client_id}</p>
              <p className="text-sm text-blue-100 mt-1">
                Member since {new Date(user.created_at).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8" data-testid="profile-details">
          <h3 className="text-2xl font-semibold mb-6">Personal Information</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {profileFields.map((field) => (
              <div key={field.testId} className="border border-gray-200 rounded-lg p-4" data-testid={field.testId}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-blue-50 p-2 rounded-lg">
                    <field.icon className="text-blue-600" size={20} />
                  </div>
                  <p className="text-sm text-gray-600 font-medium">{field.label}</p>
                </div>
                <p className="text-lg font-semibold text-gray-900 ml-11">{field.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Security Section */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8" data-testid="security-section">
          <h3 className="text-2xl font-semibold mb-6">Security</h3>
          <div className="space-y-4">
            <Link 
              to="/change-password"
              className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition" 
              data-testid="change-password-button"
            >
              <div className="flex items-center gap-3">
                <Shield className="text-blue-600" size={24} />
                <div className="text-left">
                  <p className="font-medium">Change Password</p>
                  <p className="text-sm text-gray-600">Update your account password</p>
                </div>
              </div>
              <span className="text-blue-600 font-medium">→</span>
            </Link>
          </div>
        </div>

        {/* Account Actions */}
        <div className="bg-white rounded-xl shadow-sm p-8" data-testid="account-actions">
          <h3 className="text-2xl font-semibold mb-6">Account Actions</h3>
          <div className="space-y-3">
            <Link 
              to="/update-profile"
              className="block w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition text-center" 
              data-testid="update-profile-button"
            >
              Update Profile Information
            </Link>
            <button className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition" data-testid="download-data-button">
              Download My Data
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;